import React, { useEffect, useRef } from "react";
import { usePreviewPlayer } from "../hooks/usePreviewPlayer";
import YoutubeMiniPlayer from "../components/YoutubeMiniPlayer";
import { claimExclusivePlayback, releaseExclusivePlayback } from "./exclusivePlayback";
import { deezerLogo } from "./artistsTableUtils";

// The leaderboard's (and homepage lists') play button. Plays the artist's
// /api/artist-top-track candidates (own upload -> Deezer -> YouTube, see
// hooks/usePreviewPlayer), moving on when one won't play, fetched lazily on
// first click rather than eagerly for every row, so loading this table
// doesn't fire ~140 simultaneous external API calls.
export default function FallbackTrackPlayer({ artistId, artistName, spotifyId, soundcloudId, youtubeId, soundcloudUrl, autoStart = false, hideButton = false, variant }) {
  const playerIdRef = useRef({}); // stable identity for the exclusive-playback singleton
  const pauseRef = useRef(() => {});
  const didAutoStart = useRef(false);

  // Exclusive playback is claimed/released when the player reports real
  // playback starting/stopping - not on click - so another row claiming it
  // (which pauses this one) can't leave this row's icon disagreeing with
  // what's actually playing.
  const player = usePreviewPlayer({
    lookup: { artistId, artistName, spotifyId, soundcloudId, youtubeId, soundcloudUrl },
    fetchOnMount: false,
    onPlay: () => claimExclusivePlayback(playerIdRef.current, () => pauseRef.current()),
    onStop: () => releaseExclusivePlayback(playerIdRef.current),
  });
  const { state, track, toggle, yt, showVideo } = player;
  pauseRef.current = player.pause;

  useEffect(() => {
    const playerId = playerIdRef.current; // capture: this ref's own identity never changes
    return () => releaseExclusivePlayback(playerId);
  }, []);

  const handleClick = (e) => {
    e.stopPropagation();
    toggle();
  };

  // In the desktop table's expanded now-playing row the play control lives in
  // the Listen column, so this player is mounted with autoStart to begin
  // playback immediately (like the mobile card) and hideButton so its own
  // circle button isn't a redundant second control.
  useEffect(() => {
    if (!autoStart || didAutoStart.current) return;
    didAutoStart.current = true;
    toggle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoStart]);

  if (state === 'none') {
    return (
      <span style={{ color: 'var(--nx-text-muted)', fontSize: hideButton ? 12 : 11 }}>
        {hideButton ? `No preview available for ${artistName}` : 'No preview available'}
      </span>
    );
  }

  // Deezer's API terms require crediting Deezer as the source whenever a
  // sample came from their preview API - see MobileArtistsTable.jsx's
  // MobileFallbackPlayer for the mobile counterpart.
  const creditLinks = track?.source === 'deezer' && track?.url ? (
    <a
      href={track.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      title="Listen on Deezer"
      style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, padding: 4, margin: -4 }}
    >
      <img src={deezerLogo} alt="Deezer" style={{ height: 18, width: 'auto', display: 'block' }} />
    </a>
  ) : null;

  if (hideButton) {
    // Stacked, not the single flex row the button variant below uses - the
    // now-playing row this renders into (artists_table.jsx) spans the full
    // table width, so the video dock (see YoutubeMiniPlayer's `inline` mode)
    // goes on its own line below the loading label/credit links rather than
    // squeezed in beside them.
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          {state === 'loading' && (
            <span style={{ color: '#8f8f8f', fontSize: 12 }}>Loading…</span>
          )}
          {/* The row's own button only opens/closes this row, so when the
              browser blocks autoplay (or the track is paused) this is the
              way to start it. */}
          {(state === 'ready' || state === 'playing') && (track || player.linkOut) && (
            <button
              type="button"
              onClick={handleClick}
              aria-label={state === 'playing' ? `Pause ${artistName}` : track ? `Play ${artistName}` : `Open ${player.linkOut.name || 'track'}`}
              style={{ width: 'auto', margin: 0, padding: '4px 10px', borderRadius: 999, border: '1px solid var(--nx-outline)', background: 'transparent', color: 'var(--nx-text)', fontSize: 12, cursor: 'pointer' }}
            >
              {state === 'playing' ? 'Pause' : track ? 'Play' : 'Open track'}
            </button>
          )}
          {state !== 'loading' && (track || player.linkOut)?.name && (
            <span style={{ color: 'var(--nx-text)', fontSize: 13 }}>{(track || player.linkOut).name}</span>
          )}
          {creditLinks}
        </div>
        {/* inline, not the floating corner dock - this row already IS the
            dedicated "now playing" box, so the video belongs inside it, not
            floating at the viewport corner. Shown once merely 'ready', not
            only once actually 'playing', since autoplay is frequently
            blocked silently by the browser. */}
        <YoutubeMiniPlayer containerRef={yt.containerRef} active={showVideo} inline />
      </div>
    );
  }

  // variant="outline": the redesign's in-row play button (DESIGN.md), a grey
  // outline circle with a light glyph. Used by the homepage's lists; the
  // leaderboard still uses the filled button below.
  if (variant === 'outline') {
    const busy = state === 'loading';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {creditLinks}
        <button
          type="button"
          onClick={handleClick}
          aria-label={`${state === 'playing' ? 'Pause' : 'Play'} ${artistName || 'preview'}`}
          aria-busy={busy || undefined}
          style={{
            width: 38, height: 38, padding: 0, borderRadius: '50%', flexShrink: 0,
            border: '1.5px solid var(--nx-outline)', background: 'transparent', color: 'var(--nx-text)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          {busy ? (
            <span style={{ width: 12, height: 12, border: '2px solid var(--nx-outline)', borderTopColor: 'var(--nx-text)', borderRadius: '50%', animation: 'artistOrbitSpin 0.8s linear infinite' }} />
          ) : state === 'playing' ? (
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" /></svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z" fill="currentColor" /></svg>
          )}
        </button>
        <YoutubeMiniPlayer containerRef={yt.containerRef} active={showVideo} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <div
        onClick={handleClick}
        title={state === 'playing' ? 'Pause' : 'Play snippet'}
        style={{
          width: 30, height: 30, borderRadius: '50%',
          background: state === 'loading' ? '#555' : '#a020f0',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: state === 'loading' ? 'wait' : 'pointer', flexShrink: 0,
          boxShadow: '0 2px 5px rgba(0,0,0,0.3)',
        }}
      >
        {state === 'loading' ? (
          <div style={{ width: 12, height: 12, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'artistOrbitSpin 0.8s linear infinite' }} />
        ) : state === 'playing' ? (
          <div style={{ display: 'flex', gap: 3 }}>
            <div style={{ width: 3, height: 10, background: '#fff', borderRadius: 1 }} />
            <div style={{ width: 3, height: 10, background: '#fff', borderRadius: 1 }} />
          </div>
        ) : (
          <div style={{ width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: '10px solid #fff', marginLeft: 3 }} />
        )}
      </div>
      {creditLinks}
      <YoutubeMiniPlayer containerRef={yt.containerRef} active={showVideo} />
    </div>
  );
}
